import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { copyCode } from "@/lib/clipboard";
import { Copy, ExternalLink } from "lucide-react";
import { couponGoUrl, fmtDate } from "@/lib/api";
import { DemoBadge } from "@/components/site/DealCard";

export const Crumbs = ({ items }) => {
  const ld = {
    "@context": "https://schema.org", "@type": "BreadcrumbList",
    itemListElement: items.map(([to, label], i) => ({ "@type": "ListItem", position: i + 1, name: label, item: window.location.origin + (to || "") })),
  };
  return (
    <nav aria-label="Breadcrumb" className="flex items-center flex-wrap gap-1 text-xs text-slate-500 mb-6" data-testid="breadcrumbs">
      <script type="application/ld+json">{JSON.stringify(ld)}</script>
      {items.map(([to, label], i) => (
        <span key={`${to || ""}|${label}`} className="flex items-center gap-1">
          {i > 0 && <ChevronRight size={12} />}
          {to && i < items.length - 1 ? <Link to={to} className="hover:text-emerald-700">{label}</Link> : <span className="text-slate-800 font-semibold line-clamp-1">{label}</span>}
        </span>
      ))}
    </nav>
  );
};

export const SectionHead = ({ title, sub, to, testid }) => (
  <div className="flex items-end justify-between gap-4 mb-6" data-testid={testid}>
    <div>
      <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">{title}</h2>
      {sub && <p className="text-sm text-slate-500 mt-1">{sub}</p>}
    </div>
    {to && <Link to={to} className="text-sm font-bold text-emerald-700 hover:underline shrink-0">View all →</Link>}
  </div>
);

export const CouponCard = ({ c }) => {
  const copy = async () => {
    await copyCode(c.code);
  };
  return (
    <div data-testid={`coupon-card-${c.id}`} className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-dashed border-amber-300 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
      <div className="space-y-1">
        <div className="flex items-center gap-2 text-xs font-bold text-amber-800 uppercase tracking-wide">{c.merchant_name} <DemoBadge show={c.is_demo} /></div>
        <p className="font-semibold text-slate-900">{c.title}</p>
        <p className="text-xs text-slate-500">{c.description} {c.expires_at && `· Valid till ${fmtDate(c.expires_at)}`}</p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {c.code ? (
          <button onClick={copy} data-testid="coupon-copy-button" className="bg-white border border-amber-400 font-mono font-bold text-amber-900 px-3 py-1.5 rounded text-sm tracking-wider uppercase flex items-center gap-2 hover:bg-amber-100 transition-colors">
            {c.code} <Copy size={14} />
          </button>
        ) : <span className="text-xs font-semibold text-slate-500">No code needed</span>}
        <a href={couponGoUrl(c.id)} target="_blank" rel="sponsored nofollow noopener" data-testid="coupon-visit-store-button"
          className="bg-amber-600 hover:bg-amber-700 text-white text-sm font-bold px-3 py-1.5 rounded flex items-center gap-1 transition-colors">
          Visit <ExternalLink size={13} />
        </a>
      </div>
    </div>
  );
};

export const Empty = ({ text, testid = "empty-state" }) => (
  <div data-testid={testid} className="text-center py-16 border border-dashed border-slate-300 rounded-xl text-slate-500 bg-white">{text}</div>
);
